/**
 * Feature-driven sonification engine.
 *
 * Two layers derived from live spectral analysis (see ``features.ts``):
 *
 * - Drone voices: one harmonic voice per detected feature. Pitch encodes
 *   wavelength (blue = high, matching photon energy), a Doppler-honest
 *   detune spread encodes velocity width (broad lines literally shimmer),
 *   gain encodes line strength, stereo position mirrors the plot (blue left).
 * - Earcon sweep: on demand (or on epoch change) an arpeggio plays one short
 *   pluck per feature in wavelength order, so the feature set of an epoch can
 *   be heard as a discrete pattern rather than a wash.
 *
 * Known lines and unidentified "discovery" features sit on separate gain
 * buses so each layer can be mixed or muted independently.
 */

import { isAudible, type LineSolo } from "../domain/lines";
import { C_KMS } from "../domain/physics";
import { velocityDetuneRatio } from "./detune";
import type { SpectralFeature, ContinuumSummary } from "./features";
import { TIME_CONSTANT_S, stopOscillator } from "./graph";

export interface FeatureLayerGains {
  lines: number;
  discovery: number;
  continuum: number;
  events: number;
}

export const DEFAULT_FEATURE_LAYER_GAINS: FeatureLayerGains = {
  lines: 0.8,
  discovery: 0.6,
  continuum: 0.35,
  events: 0.8,
};

/** Where an in-flight sweep is, sampled against the audio clock. */
export interface SweepProgress {
  /** Wavelength of the pluck sounding now, null between plucks or once finished. */
  lambdaA: number | null;
  finished: boolean;
}

/** Internal per-pluck record of an in-flight sweep, retimed on rate change. */
interface SweepEntry {
  feature: SpectralFeature;
  strength: number;
  at: number;
  stopAt: number;
  cancel: () => void;
}

const SWEEP_STEP_S = 0.16;

/** Pitch anchors: rest wavelength (Å) → frequency (Hz), log–log interpolation. */
const PITCH_RED_A = 7100;
const PITCH_BLUE_A = 4200;
const PITCH_RED_HZ = 196.0; // G3
const PITCH_BLUE_HZ = 784.0; // G5

/** Wavelength (Å) → drone/earcon frequency (Hz). Blue is high-pitched. */
export function wavelengthToFrequency(lambdaA: number): number {
  const t =
    (Math.log(PITCH_RED_A) - Math.log(lambdaA)) /
    (Math.log(PITCH_RED_A) - Math.log(PITCH_BLUE_A));
  const clamped = Math.max(-0.2, Math.min(1.2, t));
  return Math.exp(
    Math.log(PITCH_RED_HZ) + clamped * (Math.log(PITCH_BLUE_HZ) - Math.log(PITCH_RED_HZ)),
  );
}

/**
 * Pitch for a feature. A known line is pitched at its rest wavelength and then
 * detuned by its measured velocity offset (exaggerated, see `detune.ts`) so a
 * drifting Hβ peak is audible; discovery peaks map straight from wavelength.
 */
export function featurePitchHz(feature: SpectralFeature): number {
  if (feature.restA === null) return wavelengthToFrequency(feature.lambdaA);
  const offsetKms = ((feature.lambdaA - feature.restA) / feature.restA) * C_KMS;
  return wavelengthToFrequency(feature.restA) / velocityDetuneRatio(offsetKms);
}

/** Wavelength → stereo position matching the plot: blue left, red right. */
function wavelengthToPan(lambdaA: number): number {
  const t = (lambdaA - PITCH_BLUE_A) / (PITCH_RED_A - PITCH_BLUE_A);
  return Math.max(-0.8, Math.min(0.8, -0.8 + 1.6 * t));
}

/**
 * Loudness for a feature: its integrated excess relative to a per-object
 * reference flux, compressed so bright-state Hα does not drown everything.
 */
function featureStrength(feature: SpectralFeature, referenceFlux: number): number {
  const ratio = feature.area / Math.max(1e-6, referenceFlux);
  return Math.min(1.2, Math.sqrt(Math.max(0, ratio)));
}

/** Fractional Doppler half-width of the line: FWHM/2 as a fraction of c. */
function doppelSpread(feature: SpectralFeature): number {
  const fwhmKms = (feature.fwhmA / feature.lambdaA) * C_KMS;
  return Math.min(0.05, fwhmKms / 2 / C_KMS);
}

interface VoiceTarget {
  feature: SpectralFeature;
  strength: number;
  known: boolean;
}

/**
 * One drone voice: three oscillators (centre plus a Doppler-spread pair) into
 * a gentle lowpass, a gain, and a stereo panner. Reused across updates via
 * wavelength matching so scrubbing glides instead of clicking.
 */
class DroneVoice {
  lambdaA: number;
  known: boolean;
  private readonly oscs: OscillatorNode[];
  private readonly filter: BiquadFilterNode;
  private readonly gain: GainNode;
  private readonly panner: StereoPannerNode;

  constructor(
    ctx: AudioContext,
    output: AudioNode,
    wave: PeriodicWave | null,
    target: VoiceTarget,
  ) {
    this.lambdaA = target.feature.lambdaA;
    this.known = target.known;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.Q.value = 0.5;
    this.gain = ctx.createGain();
    this.gain.gain.value = 0;
    this.panner = ctx.createStereoPanner();

    this.oscs = [0, -1, 1].map(() => {
      const osc = ctx.createOscillator();
      if (wave) osc.setPeriodicWave(wave);
      else osc.type = "triangle";
      osc.connect(this.filter);
      osc.start();
      return osc;
    });

    this.filter.connect(this.gain);
    this.gain.connect(this.panner);
    this.panner.connect(output);
    this.apply(target, ctx.currentTime, true);
  }

  apply(target: VoiceTarget, atTime: number, immediate = false): void {
    this.lambdaA = target.feature.lambdaA;
    const f0 = featurePitchHz(target.feature);
    const spread = doppelSpread(target.feature);
    const freqs = [f0, f0 * (1 - spread), f0 * (1 + spread)];
    // The centre oscillator carries the line; the spread pair fades in with
    // width so narrow lines stay pure and broad lines audibly shimmer.
    for (let i = 0; i < this.oscs.length; i += 1) {
      if (immediate) this.oscs[i].frequency.value = freqs[i];
      else this.oscs[i].frequency.setTargetAtTime(freqs[i], atTime, TIME_CONSTANT_S);
    }
    const cutoff = Math.min(8000, f0 * (2.5 + 4 * target.strength));
    const level = 0.14 * target.strength;
    const pan = wavelengthToPan(target.feature.lambdaA);
    if (immediate) {
      this.filter.frequency.value = cutoff;
      this.panner.pan.value = pan;
    } else {
      this.filter.frequency.setTargetAtTime(cutoff, atTime, TIME_CONSTANT_S);
      this.panner.pan.setTargetAtTime(pan, atTime, TIME_CONSTANT_S);
    }
    this.gain.gain.setTargetAtTime(level, atTime, TIME_CONSTANT_S);
  }

  fadeOut(atTime: number): void {
    this.gain.gain.setTargetAtTime(0, atTime, TIME_CONSTANT_S);
  }

  dispose(): void {
    for (const osc of this.oscs) {
      stopOscillator(osc);
      osc.disconnect();
    }
    this.filter.disconnect();
    this.gain.disconnect();
    this.panner.disconnect();
  }
}

export class FeatureEngine {
  private readonly ctx: AudioContext;
  private readonly trim: GainNode;
  private readonly linesBus: GainNode;
  private readonly discoveryBus: GainNode;
  private readonly continuumBus: GainNode;
  private readonly eventsBus: GainNode;
  private readonly knownWave: PeriodicWave;
  private voices: DroneVoice[] = [];
  private padLow: OscillatorNode;
  private padHigh: OscillatorNode;
  private padLowGain: GainNode;
  private padHighGain: GainNode;
  private active = false;
  private scheduledEarcons: Array<{ oscs: OscillatorNode[]; gain: GainNode }> = [];
  /** 1 = default tempo; 0.5–2 = half to double speed. Applied live to in-flight sweeps. */
  private sweepRate = 1;
  private activeSweep: SweepEntry[] | null = null;

  constructor(ctx: AudioContext, output: AudioNode) {
    this.ctx = ctx;
    this.trim = ctx.createGain();
    this.trim.gain.value = 0;
    this.trim.connect(output);

    const makeBus = (initial: number): GainNode => {
      const bus = ctx.createGain();
      bus.gain.value = initial;
      bus.connect(this.trim);
      return bus;
    };
    this.linesBus = makeBus(DEFAULT_FEATURE_LAYER_GAINS.lines);
    this.discoveryBus = makeBus(DEFAULT_FEATURE_LAYER_GAINS.discovery);
    this.continuumBus = makeBus(DEFAULT_FEATURE_LAYER_GAINS.continuum);
    this.eventsBus = makeBus(DEFAULT_FEATURE_LAYER_GAINS.events);

    // Warm organ-ish partial stack for known lines; discovery voices use a
    // plain triangle so the two populations are told apart by ear.
    const real = new Float32Array([0, 1, 0.45, 0.28, 0.16, 0.09, 0.05]);
    const imag = new Float32Array(real.length);
    this.knownWave = ctx.createPeriodicWave(real, imag);

    // Continuum pad: a low warm tone (red weight) and a brighter fifth-plus-
    // octave partner (blue weight); the measured band levels crossfade them.
    const buildPad = (freq: number): [OscillatorNode, GainNode] => {
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = freq;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain);
      gain.connect(this.continuumBus);
      osc.start();
      return [osc, gain];
    };
    [this.padLow, this.padLowGain] = buildPad(98.0); // G2
    [this.padHigh, this.padHighGain] = buildPad(392.0); // G4
  }

  /** Audible or not. An inactive engine also refuses to start a sweep. */
  setActive(active: boolean): void {
    this.active = active;
    this.trim.gain.setTargetAtTime(active ? 1 : 0, this.ctx.currentTime, TIME_CONSTANT_S);
  }

  setLayerGains(gains: FeatureLayerGains): void {
    const now = this.ctx.currentTime;
    this.linesBus.gain.setTargetAtTime(gains.lines, now, TIME_CONSTANT_S);
    this.discoveryBus.gain.setTargetAtTime(gains.discovery, now, TIME_CONSTANT_S);
    this.continuumBus.gain.setTargetAtTime(gains.continuum, now, TIME_CONSTANT_S);
    this.eventsBus.gain.setTargetAtTime(gains.events, now, TIME_CONSTANT_S);
  }

  /**
   * Retarget the drone layer at a new feature set. Voices are matched to
   * features by wavelength proximity so a feature that persists across epochs
   * keeps its voice (parameters glide); unmatched voices fade out and new
   * features get fresh voices.
   */
  update(
    features: SpectralFeature[],
    summary: ContinuumSummary,
    referenceFlux: number,
    continuumScale: number,
    solo: LineSolo,
  ): void {
    const now = this.ctx.currentTime;

    const targets: VoiceTarget[] = features
      .filter((f) => isAudible(f.knownKey, solo))
      .map((f) => ({
        feature: f,
        strength: featureStrength(f, referenceFlux),
        known: f.knownKey !== null,
      }));

    const unmatched = new Set(this.voices);
    const assignments = new Map<VoiceTarget, DroneVoice>();
    for (const target of targets) {
      let best: DroneVoice | null = null;
      let bestDist = 25; // Å — beyond this a feature is "new", not "moved"
      for (const voice of unmatched) {
        if (voice.known !== target.known) continue;
        const dist = Math.abs(voice.lambdaA - target.feature.lambdaA);
        if (dist < bestDist) {
          bestDist = dist;
          best = voice;
        }
      }
      if (best) {
        unmatched.delete(best);
        assignments.set(target, best);
      }
    }

    for (const voice of unmatched) {
      voice.fadeOut(now);
    }
    // Dispose faded voices on the next update cycle rather than tracking
    // release tails; a small pool keeps node churn negligible.
    const surviving: DroneVoice[] = [];
    for (const target of targets) {
      const existing = assignments.get(target);
      if (existing) {
        existing.apply(target, now);
        surviving.push(existing);
      } else {
        const bus = target.known ? this.linesBus : this.discoveryBus;
        surviving.push(
          new DroneVoice(this.ctx, bus, target.known ? this.knownWave : null, target),
        );
      }
    }
    // Give released voices their fade tail before tearing nodes down.
    for (const voice of unmatched) {
      const v = voice;
      setTimeout(() => v.dispose(), 600);
    }
    this.voices = surviving;

    const blue = Number.isFinite(summary.blueLevel) ? Math.max(0, summary.blueLevel) : 0;
    const red = Number.isFinite(summary.redLevel) ? Math.max(0, summary.redLevel) : 0;
    const level = Math.min(1.2, (blue + red) * 0.5 * continuumScale);
    const total = blue + red;
    const blueFrac = total > 0 ? blue / total : 0.5;
    const padScale = isAudible("continuum", solo) ? 0.2 * level : 0;
    this.padLowGain.gain.setTargetAtTime(padScale * (1 - blueFrac) * 1.4, now, TIME_CONSTANT_S);
    this.padHighGain.gain.setTargetAtTime(padScale * blueFrac, now, TIME_CONSTANT_S);
  }

  /**
   * Play the earcon sweep: one pluck per feature, red to blue (ascending
   * pitch), pitch/pan as the drone layer, loudness from strength, envelope
   * from width — narrow features ping, broad features swell and linger.
   */
  playSweep(
    features: SpectralFeature[],
    referenceFlux: number,
    solo: LineSolo,
  ): boolean {
    if (!this.active) return false;
    // Replace, never overlap: an epoch change mid-sweep restarts cleanly.
    this.cancelSweep();
    const ordered = [...features]
      .filter((f) => isAudible(f.knownKey, solo))
      .sort((f1, f2) => f2.lambdaA - f1.lambdaA);
    const step = SWEEP_STEP_S / this.sweepRate;
    let at = this.ctx.currentTime + 0.05;
    const entries: SweepEntry[] = [];
    for (const feature of ordered) {
      const strength = featureStrength(feature, referenceFlux);
      const scheduled = this.scheduleEarcon(feature, strength, at);
      entries.push({ feature, strength, at, ...scheduled });
      at += step;
    }
    this.activeSweep = entries.length > 0 ? entries : null;
    return this.activeSweep !== null;
  }

  /**
   * Set sweep tempo (1 = default, 0.5 = half speed, 2 = double). An in-flight
   * sweep is retimed live: plucks that have already sounded keep their place,
   * pending plucks are rescheduled at the new spacing from the current
   * position — the playback head never resets.
   */
  setSweepRate(rate: number): void {
    const clamped = Math.min(2, Math.max(0.5, rate));
    if (clamped === this.sweepRate) return;
    this.sweepRate = clamped;
    if (!this.activeSweep) return;
    const now = this.ctx.currentTime;
    const step = SWEEP_STEP_S / this.sweepRate;
    const entries = this.activeSweep;
    // Anchor on the last pluck already committed to the audio clock; only
    // plucks strictly in the future are cancelled and rescheduled.
    let anchorIdx = -1;
    for (let i = 0; i < entries.length; i += 1) {
      if (entries[i].at <= now + 0.02) anchorIdx = i;
    }
    let prevAt = anchorIdx >= 0 ? entries[anchorIdx].at : entries[0].at - step;
    for (let i = anchorIdx + 1; i < entries.length; i += 1) {
      const entry = entries[i];
      entry.cancel();
      const at = Math.max(now + 0.03, prevAt + step);
      const scheduled = this.scheduleEarcon(entry.feature, entry.strength, at);
      entry.at = at;
      entry.stopAt = scheduled.stopAt;
      entry.cancel = scheduled.cancel;
      prevAt = at;
    }
  }

  /**
   * Sample the in-flight sweep against the audio clock. Each pluck's highlight
   * holds until the next pluck takes over; the last one holds for a capped
   * slice of its own release tail. Null when no sweep is in flight.
   */
  sweepProgress(): SweepProgress | null {
    const entries = this.activeSweep;
    if (!entries) return null;
    const now = this.ctx.currentTime;
    const step = SWEEP_STEP_S / this.sweepRate;
    let lambdaA: number | null = null;
    let lastEnd = 0;
    for (let i = 0; i < entries.length; i += 1) {
      const entry = entries[i];
      const nextStart = i + 1 < entries.length ? entries[i + 1].at : entry.at + step;
      let end = Math.min(entry.stopAt, nextStart);
      if (i === entries.length - 1) end = Math.min(entry.at + 0.8, end + 0.4);
      if (now >= entry.at && now < end) lambdaA = entry.feature.lambdaA;
      lastEnd = end;
    }
    return { lambdaA, finished: now >= lastEnd };
  }

  /** Fade and stop any earcons still scheduled from a previous sweep. */
  cancelSweep(): void {
    const now = this.ctx.currentTime;
    for (const entry of this.scheduledEarcons) {
      entry.gain.gain.cancelScheduledValues(now);
      entry.gain.gain.setTargetAtTime(0, now, 0.01);
      for (const osc of entry.oscs) {
        stopOscillator(osc, now + 0.06);
      }
    }
    this.scheduledEarcons = [];
    this.activeSweep = null;
  }

  private scheduleEarcon(
    feature: SpectralFeature,
    strength: number,
    at: number,
  ): { stopAt: number; cancel: () => void } {
    const ctx = this.ctx;
    const known = feature.knownKey !== null;
    const f0 = featurePitchHz(feature);
    const widthNorm = Math.min(1, feature.baseWidthA / 400);
    const attack = 0.005 + 0.06 * widthNorm;
    const decay = 0.25 + 0.9 * widthNorm;
    const peak = 0.25 * Math.max(0.15, Math.min(1.2, strength));

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(peak, at + attack);
    gain.gain.exponentialRampToValueAtTime(0.001, at + attack + decay);
    const panner = ctx.createStereoPanner();
    panner.pan.value = wavelengthToPan(feature.lambdaA);
    gain.connect(panner);
    panner.connect(this.eventsBus);

    const oscs: OscillatorNode[] = [];
    const main = ctx.createOscillator();
    if (known) main.setPeriodicWave(this.knownWave);
    else main.type = "triangle";
    main.frequency.value = f0;
    main.connect(gain);
    oscs.push(main);
    if (!known) {
      // Discovery earcons get a soft inharmonic partial — a bell-like tell
      // that this peak is not one of the labelled lines.
      const bell = ctx.createOscillator();
      bell.type = "sine";
      bell.frequency.value = f0 * 2.76;
      const bellGain = ctx.createGain();
      bellGain.gain.value = 0.25;
      bell.connect(bellGain);
      bellGain.connect(gain);
      oscs.push(bell);
    }
    const stopAt = at + attack + decay + 0.1;
    const entry = { oscs, gain };
    this.scheduledEarcons.push(entry);
    for (const osc of oscs) {
      osc.start(at);
      osc.stop(stopAt);
      osc.onended = () => {
        osc.disconnect();
        gain.disconnect();
        panner.disconnect();
        const i = this.scheduledEarcons.indexOf(entry);
        if (i !== -1) this.scheduledEarcons.splice(i, 1);
      };
    }
    const cancel = () => {
      const t = this.ctx.currentTime;
      gain.gain.cancelScheduledValues(t);
      gain.gain.setTargetAtTime(0, t, 0.01);
      for (const osc of oscs) {
        stopOscillator(osc, t + 0.06);
      }
    };
    return { stopAt, cancel };
  }

  dispose(): void {
    for (const voice of this.voices) voice.dispose();
    this.voices = [];
    for (const osc of [this.padLow, this.padHigh]) {
      stopOscillator(osc);
      osc.disconnect();
    }
    this.padLowGain.disconnect();
    this.padHighGain.disconnect();
    this.linesBus.disconnect();
    this.discoveryBus.disconnect();
    this.continuumBus.disconnect();
    this.eventsBus.disconnect();
    this.trim.disconnect();
  }
}
